import React, { useState, useEffect, lazy, Suspense, memo, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { keyframes } from '@emotion/react';
import {
  Box,
  Heading,
  Text,
  Flex,
  Button,
  Stepper,
  Step,
  StepIndicator,
  StepStatus,
  StepIcon,
  StepNumber,
  StepTitle,
  StepDescription,
  StepSeparator,
  useToast,
  useColorModeValue,
  Container,
  Progress,
  IconButton,
  Divider,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  HStack,
  VStack
} from '@chakra-ui/react';
import { FaArrowLeft, FaArrowRight, FaSave, FaHome, FaExclamationTriangle } from 'react-icons/fa';

// Import template services
import { getTemplateById } from '../Templates/TemplateStorageService';
import { applyTemplate } from '../Templates/TemplateExtractor';

// Lazy load step components
const GameSetup = lazy(() => import('./Steps/GameSetup'));
const VisualCustomization = lazy(() => import('./Steps/VisualCustomization'));
const WinningSymbols = lazy(() => import('./Steps/WinningSymbols'));
const BountyPoolCreation = lazy(() => import('./Steps/PrizePoolCreation'));
const BatchConfiguration = lazy(() => import('./Steps/BatchConfiguration'));
const LotteryConfiguration = lazy(() => import('./Steps/LotteryConfiguration'));
const ReviewAndPublish = lazy(() => import('./Steps/ReviewAndPublish'));

// Lazy load preview component
const LiveGamePreview = lazy(() => import('./Preview/LiveGamePreview'));

// Loading component
const LoadingFallback = () => (
  <Box 
    display="flex" 
    justifyContent="center" 
    alignItems="center" 
    height="300px"
    width="100%"
  >
    <VStack spacing={4}>
      <Progress size="xs" isIndeterminate width="200px" colorScheme="blue" />
      <Text color="white">Loading component...</Text>
    </VStack>
  </Box>
);

/**
 * GameCreationWizard
 * 
 * A multi-step wizard for creating scratch card games.
 * Supports starting from a template or from scratch.
 */
const GameCreationWizard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  
  // Define steps - simplified and restructured based on new requirements
  // Memoize steps array to prevent unnecessary re-renders
  const steps = useMemo(() => [
    { title: 'Game Setup', description: 'Basic information & grid' },
    { title: 'Visual Design', description: 'Colors & themes' },
    { title: 'Winning Symbols', description: 'Custom symbols & prizes' },
    { title: 'Bounty Pool', description: 'Mint size & prizes' },
    { title: 'Batch System', description: 'Virtual deck & NFT minting' },
    { title: 'Lottery', description: 'Ticket allocation' },
    { title: 'Review & Publish', description: 'Final check' }
  ], []);
  
  // State
  const [currentStep, setCurrentStep] = useState(0);
  const [gameConfig, setGameConfig] = useState({
    id: `game-${Date.now()}`,
    title: 'PALMHEIST GOLD',
    description: 'Win up to 2,000,000 USDC',
    baseConfig: {
      gameType: 'palmheist_gold',
      bgColor: '#1A202C',
      accentColor: '#4A5568',
      cost: 10,
      maxPrize: 2000000
    },
    validationErrors: {},
    layout: {
      gridSize: { rows: 4, columns: 4 }, // 4x4 grid for Vegas VIP
      cardRatio: '3:4',
      winningPattern: 'match3',
      revealAnimation: 'scratch'
    },
    gameLogic: {
      winningSymbols: ['👑', '👑', '👑', '👑'],
      prizeValues: ['10K', '50K', '100K', '2M'],
      circleSymbol: '👑',
      gridSymbol: '👑'
    }
  });
  
  // State for validation errors
  const [validationErrors, setValidationErrors] = useState({});
  
  // Load template if specified
  useEffect(() => {
    const loadTemplate = async () => {
      // Check if template ID is provided in URL params or location state
      const templateId = searchParams.get('template') || (location.state && location.state.templateId);
      
      if (templateId) {
        try {
          // Get template from storage
          const template = await getTemplateById(templateId);
          
          if (template) {
            // Apply template to game config
            const configWithTemplate = applyTemplate(template);
            setGameConfig(configWithTemplate);
            
            toast({
              title: 'Template Loaded',
              description: `Template "${template.name}" has been applied.`,
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
          } else {
            toast({
              title: 'Template Not Found',
              description: 'The specified template could not be found.',
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
          }
        } catch (error) {
          console.error('Error loading template:', error);
          
          toast({
            title: 'Error Loading Template',
            description: error.message || 'An error occurred while loading the template.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      }
    };
    
    loadTemplate();
  }, [searchParams, location.state, toast]);
  
  // Handle update game config
  // Debounce updates to prevent excessive re-renders
  const handleUpdateGameConfig = useMemo(() => {
    let timeout;
    return (updates) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        setGameConfig(prev => ({
          ...prev,
          ...updates
        }));
      }, 10); // Small timeout to batch multiple updates
    };
  }, []);
  
  // Validate current step
  const validateCurrentStep = () => {
    const errors = {};
    
    switch (currentStep) {
      case 0: // Game Setup
        if (!gameConfig.title) {
          errors.title = 'Title is required';
        }
        if (!gameConfig.description) {
          errors.description = 'Description is required';
        }
        break;
        
      case 1: // Visual Customization
        // No validation needed for this step
        break;
        
      case 2: // Winning Symbols
        if (!gameConfig.gameLogic?.winningSymbols || gameConfig.gameLogic.winningSymbols.length === 0) {
          errors.winningSymbols = 'At least one winning symbol is required';
        }
        if (!gameConfig.gameLogic?.prizeValues || gameConfig.gameLogic.prizeValues.filter(p => p).length === 0) {
          errors.prizeValues = 'At least one prize value is required';
        }
        break;
        
      case 3: // Bounty Pool
        // Validate mint size and prize tiers
        if (!gameConfig.mintSize || gameConfig.mintSize < 10) {
          errors.mintSize = 'Valid mint size is required (minimum 10)';
        }
        if (!gameConfig.prizeTiers || gameConfig.prizeTiers.length < 1) {
          errors.prizeTiers = 'At least one prize tier is required';
        }
        break;
        
      case 4: // Lottery Configuration
        // Validate lottery tiers
        if (!gameConfig.lotteryTiers || gameConfig.lotteryTiers.length < 1) {
          errors.lotteryTiers = 'At least one lottery tier is required';
        }
        break;
        
      default:
        // No validation for other steps
        break;
    }
    
    return errors;
  };
  
  // Handle next step
  const handleNext = () => {
    // Validate current step before proceeding
    const errors = validateCurrentStep();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      toast({
        title: 'Validation errors',
        description: 'Please fix the errors before proceeding.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Preload the next step component
    const nextStep = Math.min(currentStep + 1, steps.length - 1);
    preloadNextStep(nextStep);
    
    setCurrentStep(nextStep);
    setValidationErrors({});
  };
  
  // Preload the next step component to improve perceived performance
  const preloadNextStep = (nextStep) => {
    switch (nextStep) {
      case 1:
        import('./Steps/VisualCustomization');
        break;
      case 2:
        import('./Steps/WinningSymbols');
        break;
      case 3:
        import('./Steps/PrizePoolCreation');
        break;
      case 4:
        import('./Steps/BatchConfiguration');
        break;
      case 5:
        import('./Steps/LotteryConfiguration');
        break;
      case 6:
        import('./Steps/ReviewAndPublish');
        break;
      default:
        break;
    }
  };
  
  // Navigate to previous step
  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };
  
  // Save draft
  const handleSaveDraft = () => {
    // Implement draft saving logic here
    // For now, just show a toast message
    toast({
      title: 'Draft Saved',
      description: 'Your game draft has been saved.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };
  
  // Handle game completion
  const handleGameComplete = (completedGame) => {
    // Navigate to the games list or wherever appropriate
    navigate('/modular-profile/games/scratch-cards');
    
    toast({
      title: 'Game Created',
      description: 'Your game has been successfully created and published!',
      status: 'success',
      duration: 5000,
      isClosable: true,
    });
  };
  
  // Colors for glassmorphism effect
  const glassBackground = useColorModeValue(
    'rgba(255, 255, 255, 0.15)', 
    'rgba(20, 20, 40, 0.25)'
  );
  const glassBorder = useColorModeValue(
    'rgba(255, 255, 255, 0.2)', 
    'rgba(100, 100, 255, 0.2)'
  );
  const highlightColor = useColorModeValue(
    'rgba(255, 255, 255, 0.05)', 
    'rgba(100, 100, 255, 0.05)'
  );
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  // Colorful gradients for each step
  const stepGradients = [
    'linear-gradient(135deg, #1a365d 0%, #2a4365 100%)', // Blue - Game Setup
    'linear-gradient(135deg, #44337a 0%, #553c9a 100%)', // Purple - Visual Design
    'linear-gradient(135deg, #702459 0%, #97266d 100%)', // Pink - Winning Symbols
    'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', // Blue - Bounty Pool
    'linear-gradient(135deg, #065f46 0%, #059669 100%)', // Green - Lottery
    'linear-gradient(135deg, #9f1239 0%, #e11d48 100%)'  // Red - Review & Publish
  ];
  
  // Get current step gradient
  const currentGradient = stepGradients[currentStep];
  
  // Render the current step content
  // Memoize to prevent unnecessary re-renders
  const renderCurrentStep = useMemo(() => {
    return () => {
    switch (currentStep) {
      case 0:
        return (
          <GameSetup 
            gameConfig={gameConfig} 
            updateGameConfig={handleUpdateGameConfig}
            errors={validationErrors}
          />
        );
        
      case 1:
        return (
          <VisualCustomization 
            gameConfig={gameConfig} 
            updateGameConfig={handleUpdateGameConfig}
            errors={validationErrors}
          />
        );
        
      case 2:
        return (
          <WinningSymbols 
            gameConfig={gameConfig} 
            updateGameConfig={handleUpdateGameConfig}
            errors={validationErrors}
          />
        );
        
      case 3:
        return (
          <BountyPoolCreation
            gameConfig={gameConfig}
            updateGameConfig={handleUpdateGameConfig}
            errors={validationErrors}
          />
        );

      case 4:
        return (
          <BatchConfiguration
            gameConfig={gameConfig}
            updateGameConfig={handleUpdateGameConfig}
            errors={validationErrors}
          />
        );

      case 5:
        return (
          <LotteryConfiguration
            gameConfig={gameConfig}
            updateGameConfig={handleUpdateGameConfig}
            errors={validationErrors}
          />
        );

      case 6:
        return (
          <ReviewAndPublish
            gameConfig={gameConfig}
            updateGameConfig={handleUpdateGameConfig}
            errors={validationErrors}
            onComplete={handleGameComplete}
          />
        );

      default:
        return <Box>Unknown step</Box>;
    }
  };
  }, [currentStep, gameConfig, validationErrors, handleUpdateGameConfig, handleGameComplete]);
  
  // Render step navigation with glassmorphism design
  const renderStepNavigation = () => {
    return (
      <Flex justify="space-between" mt={8}>
        <Button
          leftIcon={<FaArrowLeft />}
          onClick={handlePrevious}
          isDisabled={currentStep === 0}
          variant="outline"
          borderColor="rgba(255, 255, 255, 0.3)"
          color="white"
          _hover={{ bg: "rgba(255, 255, 255, 0.1)" }}
        >
          Previous
        </Button>
        
        <HStack spacing={4}>
          <Button
            leftIcon={<FaSave />}
            onClick={handleSaveDraft}
            variant="outline"
            borderColor="rgba(255, 255, 255, 0.3)"
            color="white"
            _hover={{ bg: "rgba(255, 255, 255, 0.1)" }}
          >
            Save Draft
          </Button>
          
          <Button
            leftIcon={<FaHome />}
            onClick={() => navigate('/modular-profile/games/scratch-cards')}
            variant="outline"
            borderColor="rgba(255, 255, 255, 0.3)"
            color="white"
            _hover={{ bg: "rgba(255, 255, 255, 0.1)" }}
          >
            Exit
          </Button>
        </HStack>
        
        {currentStep < steps.length - 1 ? (
          <Button
            rightIcon={<FaArrowRight />}
            onClick={handleNext}
            bg="rgba(255, 255, 255, 0.2)"
            color="white"
            _hover={{ bg: "rgba(255, 255, 255, 0.3)" }}
          >
            Next
          </Button>
        ) : (
          <Button
            onClick={handleNext}
            bg="rgba(255, 255, 255, 0.2)"
            color="white"
            _hover={{ bg: "rgba(255, 255, 255, 0.3)" }}
          >
            Finish
          </Button>
        )}
      </Flex>
    );
  };
  
  // Animation for the progress bar
  const progressAnimation = keyframes`
    from { width: 0; }
    to { width: 100%; }
  `;
  
  // Function to scroll to a step section
  const scrollToStep = (index) => {
    const stepElement = document.getElementById(`step-${index}`);
    if (stepElement) {
      stepElement.scrollIntoView({ behavior: 'smooth' });
    }
    setCurrentStep(index);
  };

  return (
    <Box 
      minH="100vh" 
      py={8}
      px={4}
    >
      <Container maxW="container.xl">
        {/* Main layout with preview on the side */}
        <Flex direction={{ base: 'column', xl: 'row' }} gap={6}>
          {/* Main content - All steps in a vertical layout */}
          <Box flex="1">
            {/* Removed horizontal step indicator */}
            
            {/* Removed vertical progress bar */}
            
            {/* Render all steps in sequence for vertical scrolling */}
            {steps.map((step, index) => (
              <Box 
                key={index} 
                id={`step-${index}`}
                mb={10}
                pt={4}
                pb={10}
                borderRadius="xl"
                bg={`${stepGradients[index]}33`} // Add transparency to the gradient
                backdropFilter="blur(10px)"
                border="1px solid rgba(255, 255, 255, 0.2)"
                boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.2)"
                overflow="hidden"
                p={6}
                color="white"
                position="relative"
                _before={{
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgGradient: stepGradients[index],
                  opacity: 0.1,
                  zIndex: -1,
                }}
              >
                <Heading size="lg" mb={6} textAlign="center">
                  {step.title}
                </Heading>
                <Text fontSize="lg" mb={6} textAlign="center">
                  {step.description}
                </Text>
                
                {/* Render the step content with Suspense for lazy loading */}
                <Suspense fallback={<LoadingFallback />}>
                  {index === 0 && (
                    <GameSetup 
                      gameConfig={gameConfig} 
                      updateGameConfig={handleUpdateGameConfig}
                      errors={validationErrors}
                    />
                  )}
                  
                  {index === 1 && (
                    <VisualCustomization 
                      gameConfig={gameConfig} 
                      updateGameConfig={handleUpdateGameConfig}
                      errors={validationErrors}
                    />
                  )}
                  
                  {index === 2 && (
                    <WinningSymbols 
                      gameConfig={gameConfig} 
                      updateGameConfig={handleUpdateGameConfig}
                      errors={validationErrors}
                    />
                  )}
                  
                  {index === 3 && (
                    <BountyPoolCreation
                      gameConfig={gameConfig}
                      updateGameConfig={handleUpdateGameConfig}
                      errors={validationErrors}
                    />
                  )}

                  {index === 4 && (
                    <BatchConfiguration
                      gameConfig={gameConfig}
                      updateGameConfig={handleUpdateGameConfig}
                      errors={validationErrors}
                    />
                  )}

                  {index === 5 && (
                    <LotteryConfiguration
                      gameConfig={gameConfig}
                      updateGameConfig={handleUpdateGameConfig}
                      errors={validationErrors}
                    />
                  )}

                  {index === 6 && (
                    <ReviewAndPublish
                      gameConfig={gameConfig}
                      updateGameConfig={handleUpdateGameConfig}
                      errors={validationErrors}
                      onComplete={handleGameComplete}
                    />
                  )}
                </Suspense>
                
                {/* Removed navigation buttons */}
              </Box>
            ))}
          </Box>
          
          {/* Preview - Sticky on the side - Only showing the card */}
          <Box 
            width={{ base: "100%", xl: "400px" }}
            position={{ base: "relative", xl: "sticky" }}
            top="0"
            alignSelf="flex-start"
            display="block"
            borderRadius="lg"
            p={4}
            mt={{ base: 0, xl: -4 }} /* Small negative margin to move it up slightly */
            mb={{ base: 8, xl: 0 }}
          >
            <LiveGamePreview gameConfig={gameConfig} />
          </Box>
        </Flex>
      </Container>
    </Box>
  );
};

export default GameCreationWizard;
